import { t } from "../i18n";
import { hrefFor } from "../app/router";
import type { StaticEvidenceProvider } from "../data/provider";
import { DiscoveryPreview } from "../components/DiscoveryPreview";
import { HeroNetwork } from "../components/HeroNetwork";
import { MiniBars, MiniNetwork } from "../components/MiniViz";
import { IconArrow } from "../components/icons";

const MODEL_PRIMARY = ["education", "skills", "technologies", "careers", "labour"] as const;
const MODEL_SECONDARY = ["gap", "learning", "development"] as const;

export function LandingPage({ provider }: { provider?: StaticEvidenceProvider | null }) {
  const skills = provider?.skills().length ?? 0;
  const careers = provider?.careers().length ?? 0;
  const emerging = provider
    ? provider.technologies().filter((x) => x.trendStatus === "emerging").length +
      provider.trends().filter((x) => x.trendStatus === "emerging").length
    : 0;
  const sources = provider?.sources().length ?? 0;
  const showMetrics = skills > 0 && careers > 0 && sources > 0;

  return (
    <article class="obs-page obs-landing">
      <header class="obs-hero">
        <div class="obs-hero-copy">
          <p class="obs-eyebrow">{t("hero.eyebrow")}</p>
          <h1>{t("hero.heading")}</h1>
          <p class="obs-lede">{t("hero.body")}</p>
          <div class="obs-actions">
            <a class="obs-btn obs-btn-primary" href={hrefFor("/radar")}>
              {t("hero.ctaRadar")}
            </a>
            <a class="obs-btn obs-btn-secondary" href={hrefFor("/skills-match")}>
              {t("hero.ctaMatch")}
            </a>
            <a class="obs-btn obs-btn-text" href={hrefFor("/methodology")}>
              {t("hero.ctaMethod")}
              <IconArrow size={16} />
            </a>
          </div>
        </div>
        <div class="obs-hero-visual">
          <HeroNetwork />
        </div>
      </header>

      <p class="obs-flow" aria-label={t("flow.label")}>
        <span>
          <b>{t("flow.explore")}</b> {t("flow.explore.detail")}
        </span>
        <span class="obs-flow-arrow" aria-hidden="true">
          →
        </span>
        <span>
          <b>{t("flow.understand")}</b> {t("flow.understand.detail")}
        </span>
        <span class="obs-flow-arrow" aria-hidden="true">
          →
        </span>
        <span>
          <b>{t("flow.develop")}</b> {t("flow.develop.detail")}
        </span>
      </p>

      <section class="obs-tools-block" aria-labelledby="obs-tools-title">
        <p class="obs-eyebrow">{t("tools.kicker")}</p>
        <h2 id="obs-tools-title">{t("tools.title")}</h2>
        <p class="obs-section-lead">{t("tools.body")}</p>
        <div class="obs-modes">
          <article class="obs-mode obs-mode--radar">
            <p class="obs-mode-kicker">{t("tools.radar.kicker")}</p>
            <h3>{t("tools.radar.title")}</h3>
            <p class="obs-mode-body">{t("tools.radar.body")}</p>
            <ul class="obs-mode-meta">
              <li>{t("tools.radar.m1")}</li>
              <li>{t("tools.radar.m2")}</li>
              <li>{t("tools.radar.m3")}</li>
            </ul>
            <MiniNetwork />
            <a class="obs-mode-cta" href={hrefFor("/radar")}>
              {t("tools.radar.cta")}
              <IconArrow size={16} />
            </a>
          </article>
          <article class="obs-mode obs-mode--match">
            <p class="obs-mode-kicker">{t("tools.match.kicker")}</p>
            <h3>{t("tools.match.title")}</h3>
            <p class="obs-mode-body">{t("tools.match.body")}</p>
            <ul class="obs-mode-meta">
              <li>{t("tools.match.m1")}</li>
              <li>{t("tools.match.m2")}</li>
              <li>{t("tools.match.m3")}</li>
            </ul>
            <MiniBars />
            <a class="obs-mode-cta" href={hrefFor("/skills-match")}>
              {t("tools.match.cta")}
              <IconArrow size={16} />
            </a>
          </article>
        </div>
      </section>

      {provider ? <DiscoveryPreview provider={provider} /> : null}

      <section class="obs-section obs-context" aria-labelledby="obs-context-title">
        <p class="obs-eyebrow">{t("context.eyebrow")}</p>
        <h2 id="obs-context-title">{t("context.title")}</h2>
        <p class="obs-section-lead">{t("context.body")}</p>
        <ul class="obs-context-points">
          <li>{t("context.m1")}</li>
          <li>{t("context.m2")}</li>
          <li>{t("context.m3")}</li>
        </ul>
      </section>

      <section class="obs-section" aria-labelledby="obs-model-title">
        <div class="obs-section-head">
          <p class="obs-eyebrow">{t("model.eyebrow")}</p>
          <h2 id="obs-model-title">{t("model.title")}</h2>
          <p class="obs-section-lead">{t("model.body")}</p>
        </div>
        <ol class="obs-model">
          {MODEL_PRIMARY.map((key) => (
            <li key={key} class="obs-model-node">
              {t(`model.${key}`)}
            </li>
          ))}
        </ol>
        <ol class="obs-model obs-model--alt">
          {MODEL_SECONDARY.map((key) => (
            <li key={key} class="obs-model-node">
              {t(`model.${key}`)}
            </li>
          ))}
        </ol>
      </section>

      {showMetrics ? (
        <section class="obs-section obs-metrics-block" aria-labelledby="obs-metrics-title">
          <div class="obs-section-head">
            <p class="obs-eyebrow">{t("metrics.eyebrow")}</p>
            <h2 id="obs-metrics-title">{t("metrics.title")}</h2>
            <p class="obs-section-lead">{t("radar.catalogNote")}</p>
          </div>
          <div class="obs-metrics">
            <div class="obs-metric">
              <b>{skills}</b>
              <span>{t("radar.metric.skills")}</span>
            </div>
            <div class="obs-metric">
              <b>{careers}</b>
              <span>{t("radar.metric.careers")}</span>
            </div>
            <div class="obs-metric">
              <b>{emerging}</b>
              <span>{t("radar.metric.emerging")}</span>
            </div>
            <div class="obs-metric">
              <b>{sources}</b>
              <span>{t("radar.metric.sources")}</span>
            </div>
            {provider ? (
              <div class="obs-metric">
                <b>{provider.resources().filter((r) => r.availability === "available").length}</b>
                <span>{t("radar.metric.resources")}</span>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <section class="obs-section obs-evidence" aria-labelledby="obs-evidence-title">
        <p class="obs-eyebrow">{t("evidence.eyebrow")}</p>
        <h2 id="obs-evidence-title">{t("evidence.title")}</h2>
        <p class="obs-section-lead">{t("evidence.body")}</p>
        <ul class="obs-pillars">
          <li>
            <strong>{t("evidence.traceable")}</strong>
            <p>{t("evidence.traceable.body")}</p>
          </li>
          <li>
            <strong>{t("evidence.structured")}</strong>
            <p>{t("evidence.structured.body")}</p>
          </li>
          <li>
            <strong>{t("evidence.evolving")}</strong>
            <p>{t("evidence.evolving.body")}</p>
          </li>
        </ul>
        <p class="obs-section-cta">
          <a class="obs-btn obs-btn-text" href={hrefFor("/methodology")}>
            {t("evidence.cta")}
            <IconArrow size={16} />
          </a>
        </p>
      </section>

      <section class="obs-section obs-learn" aria-labelledby="obs-learn-title">
        <div class="obs-section-head">
          <p class="obs-eyebrow">{t("learn.eyebrow")}</p>
          <h2 id="obs-learn-title">{t("learn.title")}</h2>
          <p class="obs-section-lead">{t("learn.body")}</p>
        </div>
        {provider ? (
          <>
            <ul class="obs-resource-list">
              {provider
                .resources()
                .filter((resource) => resource.availability === "available")
                .map((resource) => (
                  <li key={resource.id}>
                    <p class="obs-eyebrow">{t("learn.available")}</p>
                    <h3>{resource.title}</h3>
                    <p>{resource.description}</p>
                    {resource.url ? (
                      <a href={resource.url} rel="noopener noreferrer">
                        {t("learn.open")}
                      </a>
                    ) : null}
                  </li>
                ))}
            </ul>
            <h3 class="obs-subhead">{t("learn.coming")}</h3>
            <ul class="obs-coming-list">
              {provider
                .resources()
                .filter((resource) => resource.availability === "coming-soon")
                .map((resource) => (
                  <li key={resource.id}>{resource.title}</li>
                ))}
            </ul>
          </>
        ) : (
          <p class="obs-notice">{t("learn.soon")}</p>
        )}
      </section>

      <section class="obs-final" aria-labelledby="obs-final-title">
        <h2 id="obs-final-title">{t("final.title")}</h2>
        <p>{t("final.body")}</p>
        <div class="obs-actions">
          <a class="obs-btn obs-btn-primary" href={hrefFor("/radar")}>
            {t("hero.ctaRadar")}
          </a>
          <a class="obs-btn obs-btn-secondary" href={hrefFor("/skills-match")}>
            {t("hero.ctaMatch")}
          </a>
        </div>
      </section>
    </article>
  );
}
